import React, { useContext, useState, useEffect } from 'react';
import Button from '../../components/shared/custom-btn';
import CustomInput from '../../components/shared/custom-input';
import { RxCross2 } from 'react-icons/rx';
import CustomTextarea from '../../components/shared/custom-textarea';
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { createVehicle, getAllVehicleModels, getAllVehicleMakes } from '../../services/vehicles-service';
import { ApiResponseMessage } from '../../components/shared/api-response';
import TabToggle from '../../components/shared/tab-toggle';
import { prependDefault } from '../../utils/structure-options-utils';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import Loader from '../../components/shared/loader';

const AddModal = ({ handleCloseAddModal, globalDataLoading, onAdd, openEditModal, vehicleTypes, vehicleBodies, vehicleMakes }) => {
    const { accountId } = useContext(AccountsContext);

    const [formData, setFormData] = useState({
        vin: '',
        name: '',
        licensePlate: '',
        make: '',
        model: '',
        year: '',
        vehicleType: '',
        bodyClass: '',
        fuelType: 'Gasoline',
        color: '',
        note: '',
    });


    const [loading, setLoading] = useState(false);
    const [vehicleModels, setVehicleModels] = useState([]);
    const [apiResponse, setApiResponse] = useState({ message: '', type: '' });
    const [localMakes, setLocalMakes] = useState(vehicleMakes || []);
    const [isMakesLoading, setIsMakesLoading] = useState(false);
    const searchTimeout = React.useRef(null);

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : '',
            // Clear model when make changes to avoid stale selection
            ...(name === 'make' ? { model: '' } : {})
        }));
        setApiResponse({ message: '', type: '' });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setApiResponse({ message: '', type: '' });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (loading || apiResponse.type === 'success') return;
        setLoading(true);
        setApiResponse({ message: '', type: '' });

        try {
            const response = await createVehicle(formData, accountId);
            if (response.success) {
                setApiResponse({ message: 'Vehicle created successfully!', type: 'success' });
                const newItem = response.data.data;
                onAdd?.(newItem);
                setTimeout(() => {
                    openEditModal?.(newItem);
                    handleCloseAddModal();
                }, 1000);
            } else {
                setApiResponse({
                    message: response.data.message || 'Failed to create vehicle',
                    type: 'error'
                });
            }
        } catch (error) {
            setApiResponse({
                message: error.message || 'An unexpected error occurred',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchVehicleModels = async () => {
        try {
            const selectedMake = vehicleMakes.find((make) => make.label === formData.make)
            const response = await getAllVehicleModels(accountId, selectedMake.makeId);
            console.log(response);
            if (response.success) {
                setVehicleModels(response.data.data || []);
            } else {
                console.log(response.data.message || 'Failed to fetch vehicles records');
                setVehicleModels([]);
            }
        } catch (error) {
            console.log('An error occurred while fetching vehicle records');
            setVehicleModels([]);
        }
    };

    const handleMakeSearch = async (inputValue) => {
        if (!inputValue) return;

        setIsMakesLoading(true);
        try {
            const response = await getAllVehicleMakes(accountId, inputValue);
            if (response.success) {
                setLocalMakes(response.data.data || []);
            }
        } catch (error) {
            console.error('Error searching makes:', error);
        } finally {
            setIsMakesLoading(false);
        }
    };

    const handleInputChange = (newValue, { action }) => {
        if (action === 'input-change') {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => {
                handleMakeSearch(newValue);
            }, 500);
        }
    };

    const handleMenuOpen = async () => {
        if (localMakes.length === 0) {
            setIsMakesLoading(true);
            try {
                const response = await getAllVehicleMakes(accountId);
                if (response.success) {
                    setLocalMakes(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching initial makes:', error);
            } finally {
                setIsMakesLoading(false);
            }
        }
    };

    // Fetch vehicle models when make changes
    useEffect(() => {
        if (formData.make) {
            fetchVehicleModels();
        } else {
            setVehicleModels([]);
        }
    }, [formData.make]);


    const vehicleTypesOptions = prependDefault(vehicleTypes.map(v => ({ value: v.slug, label: v.label })), 'Types');
    const vehicleBodiesOptions = prependDefault(vehicleBodies.map(v => ({ value: v.slug, label: v.label })), 'Bodies');
    const vehicleMakesOptions = prependDefault(localMakes.map(v => ({ value: v.label, label: v.label })), 'Makes');
    const vehicleModelsOptions = prependDefault(vehicleModels.map(v => ({ value: v.label, label: v.label })), 'Models');

    return (
        <form onSubmit={handleSave} className="space-y-2 pt-3 relative">
            <ModalHeading className="text-left mb-3 border-b border-gray-300 px-4 pb-2">
                Add Vehicle
            </ModalHeading>

            <button
                className='hover:scale-105 absolute -top-2 right-2 bg-white border p-2 rounded-full'
                onClick={handleCloseAddModal}
                disabled={loading}
            >
                <RxCross2 className='text-xl' />
            </button>

            <ApiResponseMessage response={apiResponse} />

            {
                globalDataLoading ?
                    <div className='py-40 flex justify-center w-full'>
                        <Loader />
                    </div>
                    :

                    <div className="grid grid-cols-1 sm:grid-cols-2 px-4 gap-y-1.5 gap-x-4">
                        <CustomInput
                            label="VIN"
                            name="vin"
                            value={formData.vin}
                            onChange={handleChange}
                            required
                            disabled={loading}
                        />
                        <CustomInput
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            disabled={loading}
                        />
                        <CustomInput
                            label="License Plate"
                            name="licensePlate"
                            value={formData.licensePlate}
                            onChange={handleChange}
                            required
                            disabled={loading}
                        />
                        <CustomReactSelect
                            label="Make"
                            name="make"
                            type="select"
                            options={vehicleMakesOptions}
                            value={vehicleMakesOptions.find(opt => opt.value === formData.make)}
                            onChange={handleSelectChange}
                            onInputChange={handleInputChange}
                            onMenuOpen={handleMenuOpen}
                            isLoading={isMakesLoading}
                            required={true}
                            isDisabled={loading}
                        />
                        <CustomReactSelect
                            label="Model"
                            name="model"
                            options={vehicleModelsOptions}
                            value={vehicleModelsOptions.find(opt => opt.value === formData.model)}
                            onChange={handleSelectChange}
                            required={true}
                            isDisabled={loading || !formData.make || vehicleModels.length === 0}
                        />
                        <CustomInput
                            label="Year"
                            name="year"
                            type="number"
                            value={formData.year}
                            onChange={handleChange}
                            required
                            disabled={loading}
                        />
                        <CustomReactSelect
                            label="Vehicle Type"
                            name="vehicleType"
                            options={vehicleTypesOptions}
                            value={vehicleTypesOptions.find(opt => opt.value === formData.vehicleType)}
                            onChange={handleSelectChange}
                            required={true}
                            isDisabled={loading}
                        />
                        <CustomReactSelect
                            label="Body Class"
                            name="bodyClass"
                            options={vehicleBodiesOptions}
                            value={vehicleBodiesOptions.find(opt => opt.value === formData.bodyClass)}
                            onChange={handleSelectChange}
                            required={true}
                            isDisabled={loading}
                        />

                        <CustomInput
                            label="Color"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            disabled={loading}
                        />

                        <div className="flex flex-col gap-0.5 sm:w-auto w-full">
                            <label className="block text-sm text-gray-700">Fuel Type</label>
                            <TabToggle
                                options={[
                                    { label: 'Gasoline', value: 'Gasoline' },
                                    { label: 'Diesel', value: 'Diesel' }
                                ]}
                                selected={formData.fuelType}
                                onChange={(val) => setFormData(prev => ({ ...prev, fuelType: val }))}
                                disabled={loading}
                            />
                        </div>

                        <div className='sm:col-span-2'>
                            <CustomTextarea
                                label="Note"
                                name="note"
                                value={formData.note}
                                onChange={handleChange}
                                rows={2}
                                disabled={loading}
                            />
                        </div>
                    </div>
            }


            <div className="flex justify-end px-4">
                <Button
                    label={'Save'}
                    loading={loading}
                    type="submit"
                    disabled={loading || apiResponse.type === 'success'}
                    loadingLabel='Saving...'
                    className="bg-btnBg rounded-lg !py-1 text-white"
                />
            </div>
        </form>
    );
};

export default AddModal;